import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import customersModelMc from "../../../models/CustomersModelMc";
import { LOAD_DATA_FAILURE } from "../../../../actions/inbox/conversationActions";
import { updateOrInsertConversationOptimized } from "../../../../actions/inbox/conversationOptimizedActions";
import { showInboxApiErrorToast } from "../utils/showInboxApiErrorToast";

function useCustomerDetailActions() {
  const { t } = useTranslation('InboxMC');

  /**
   * Redux dispatcher instance to send actions
   * @type {function}
   */
  const dispatch = useDispatch();

  /**
   * Loading state during API operations
   * @type {[boolean, function]}
   */
  const [loading, setLoading] = useState(false);

  /**
   * Selector to get the selected conversation ID from the Redux store
   * @type {string|null}
   */
  const selectedConversationId = useSelector(
    (reduxState) => reduxState.conversationsReducer.selectedConversationId
  );

  /**
   * Function to save customer data and update the conversation in the Redux state
   * @param {Object} formData - Customer data to save
   * @param {Object} [options] - Additional options for callbacks
   * @param {function} [options.onSuccess] - Callback to execute in case of success
   * @param {function} [options.onError] - Callback to execute in case of error
   * @returns {Promise<Object>} Result of the operation with success or error
   *
   * @description
   * This function handles the complete process of saving customer data, including:
   * 1. Activate the loading state.
   * 2. Execute the API call to save the customer data.
   * 3. Update the conversation in the Redux state only after a successful save.
   * 4. Handle errors and execute callbacks as appropriate.
   * 5. Deactivate the loading state after the operation is finished.
   *
   * @example
   * const { saveCustomer } = useCustomerDetailActions();
   * const result = await saveCustomer(formData, {
   *   onSuccess: (res) => console.log('Customer saved', res),
   *   onError: (err) => console.error('Error saving customer', err)
   * });
   */
  const saveCustomer = useCallback(async (formData, { onSuccess, onError } = {}) => {
    // Activate the loading state
    setLoading(true);

    try {
      // Execute save via API
      const result = await customersModelMc.save(formData);

      // Update conversation in the Redux state (ONLY after successful save)
      const updateResult = await dispatch(updateOrInsertConversationOptimized({
        id: selectedConversationId,
        changes: formData
      }));

      if (!updateResult.success) {
        const errorMessage = showInboxApiErrorToast(
          updateResult.error,
          t('customerDetails.errors.saveFailed')
        );
        dispatch({
          type: LOAD_DATA_FAILURE,
          error: errorMessage
        });
      }

      // Execute success callback if provided and is a valid function
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess(result);
      }

      // Return successful result
      return { success: true, data: result };
    } catch (error) {
      const errorMessage = showInboxApiErrorToast(
        error,
        t('customerDetails.errors.saveFailed')
      );

      dispatch({
        type: LOAD_DATA_FAILURE,
        error: errorMessage
      });

      if (onError && typeof onError === 'function') {
        onError(error);
      }

      return { success: false, error: errorMessage };
    } finally {
      // Deactivate loading state in any case
      setLoading(false);
    }
  }, [dispatch, selectedConversationId, t]);

  /**
   * Memoized return value of the hook
   *
   * @type {UseCustomerDetailActionsReturn}
   * @memoized
   *
   * @description
   * Memoized object that contains all operations and states of the hook.
   * Re-memoizes only when loading or saveCustomer changes to optimize renders.
   *
   * @properties
   * @property {boolean} loading - Loading state during API operations
   * @property {function} saveCustomer - Memoized function to save customer
   */
  const returnValue = useMemo(() => ({
    loading,
    saveCustomer
  }), [loading, saveCustomer]);

  return returnValue;
}

export default useCustomerDetailActions;
